import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';

import { ApiEndpoints, ApiService } from '@nano/core/api';
import { ClassifierResponse, Classifier, CountryGroup, GlossaryId } from './classifier';

@Injectable({ providedIn: 'root' })
export class ClassifierService {
  private readonly glossaryCache = new Map<string, Observable<Classifier[]>>();

  constructor(
    protected apiService: ApiService,
    @Inject(L10N_LOCALE) private locale: L10nLocale
  ) {}

  getCountries(countryGroup: CountryGroup): Observable<Classifier[]> {
    return this.apiService
      .get<ClassifierResponse>(ApiEndpoints.classifierCountries(countryGroup))
      .pipe(map((classifierResponse: ClassifierResponse) => classifierResponse.response));
  }

  getGlossary(glossaryId: GlossaryId | string): Observable<Classifier[]> {
    const cacheKey = `${this.locale.language || 'default'}:${glossaryId}`;
    const cachedGlossary = this.glossaryCache.get(cacheKey);

    if (cachedGlossary) {
      return cachedGlossary;
    }

    const glossary$ = this.apiService.get<ClassifierResponse>(ApiEndpoints.classifierGlossaries(glossaryId)).pipe(
      map((classifierResponse: ClassifierResponse) => classifierResponse.response),
      catchError((error: unknown) => {
        this.glossaryCache.delete(cacheKey);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.glossaryCache.set(cacheKey, glossary$);
    return glossary$;
  }
}
